import * as React from 'react';
import StringField from '../../../core/components/Fields/StringField.tsx';
import TextField from '../../../core/components/Fields/TextField.tsx';
import IntegerField from '../../../core/components/Fields/IntegerField.tsx';
import DecimalField from '../../../core/components/Fields/DecimalField.tsx';
import BooleanField from '../../../core/components/Fields/BooleanField.tsx';
import SelectField from '../../../core/components/Fields/SelectField.tsx';
import TagsField from '../../../core/components/Fields/TagsField.tsx';
import JsonField from '../../../core/components/Fields/JsonField.tsx';
import FormField from '../../../core/components/FormField.tsx';

export type SchemaProperty = {
    type?: string;
    title?: string;
    description?: string;
    enum?: string[];
    default?: any;
    items?: SchemaProperty;
    'x-widget'?: 'textarea';
    anyOf?: SchemaProperty[];
};

export type JsonSchema = {
    title?: string;
    properties: Record<string, SchemaProperty>;
    required?: string[];
};

const humanize = (name: string): string =>
    name
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase())
        .replace(/\s+/g, ' ')
        .trim();

const isStringSchema = (prop: SchemaProperty): boolean => resolveType(prop) === 'string';
const isIntegerSchema = (prop: SchemaProperty): boolean => resolveType(prop) === 'integer';
const isNumberSchema = (prop: SchemaProperty): boolean => resolveType(prop) === 'number';
const isBooleanSchema = (prop: SchemaProperty): boolean => resolveType(prop) === 'boolean';
const isArrayOfStringSchema = (prop: SchemaProperty): boolean =>
    resolveType(prop) === 'array' && (prop.items?.type === 'string' || resolveType(prop.items || {}) === 'string');

// Pydantic emits Optional[T] as { anyOf: [{type: T}, {type: 'null'}] }. Unwrap to the non-null branch.
function resolveType(prop: SchemaProperty): string | undefined {
    if (prop.type) return prop.type;
    if (prop.anyOf) {
        const nonNull = prop.anyOf.find((p) => p.type && p.type !== 'null');
        return nonNull?.type;
    }
    return undefined;
}

export type FieldError = { path: string; message: string };

export interface SchemaFormProps {
    schema: JsonSchema;
    value: Record<string, any>;
    onChange: (next: Record<string, any>) => void;
    errors?: FieldError[];
    disabled?: boolean;
}

const SchemaForm = ({ schema, value, onChange, errors, disabled }: SchemaFormProps) => {
    const errorByPath = React.useMemo(() => {
        const map: Record<string, string> = {};
        (errors || []).forEach((e) => {
            map[e.path] = e.message;
        });
        return map;
    }, [errors]);

    const setField = (name: string, fieldValue: any) => {
        onChange({ ...value, [name]: fieldValue });
    };

    const propertyEntries = Object.entries(schema.properties || {});

    return (
        <div className="flex flex-col gap-4">
            {propertyEntries.map(([name, prop]) => {
                const label = prop.title || humanize(name);
                const helpText = prop.description;
                const error = errorByPath[name] ? { message: errorByPath[name] } : undefined;
                const fieldValue = value?.[name];

                return (
                    <FormField key={name} label={label} helpText={helpText} error={error} disabled={disabled}>
                        {renderControl({
                            name,
                            prop,
                            value: fieldValue,
                            onChange: (next) => setField(name, next),
                            disabled,
                        })}
                    </FormField>
                );
            })}
        </div>
    );
};

const renderControl = ({
    name,
    prop,
    value,
    onChange,
    disabled,
}: {
    name: string;
    prop: SchemaProperty;
    value: any;
    onChange: (next: any) => void;
    disabled?: boolean;
}) => {
    if (isStringSchema(prop) && prop.enum) {
        return (
            <SelectField
                value={value ?? ''}
                onValueChange={(v) => onChange(v)}
                disabled={disabled}
                options={prop.enum.map((opt) => ({ value: opt, label: opt }))}
            />
        );
    }

    if (isStringSchema(prop) && prop['x-widget'] === 'textarea') {
        return (
            <TextField
                value={value ?? ''}
                onValueChange={(v) => onChange(v)}
                disabled={disabled}
                inputClassName="min-h-48 h-48"
            />
        );
    }

    if (isStringSchema(prop)) {
        return <StringField value={value ?? ''} onValueChange={(v) => onChange(v)} disabled={disabled} />;
    }

    if (isIntegerSchema(prop)) {
        return (
            <IntegerField
                value={value !== undefined && value !== null ? String(value) : ''}
                onValueChange={(v) => {
                    const parsed = parseInt(v as string, 10);
                    onChange(Number.isNaN(parsed) ? null : parsed);
                }}
                disabled={disabled}
            />
        );
    }

    if (isNumberSchema(prop)) {
        return (
            <DecimalField
                value={value !== undefined && value !== null ? String(value) : ''}
                onValueChange={(v) => {
                    const parsed = parseFloat(v as string);
                    onChange(Number.isNaN(parsed) ? null : parsed);
                }}
                disabled={disabled}
            />
        );
    }

    if (isBooleanSchema(prop)) {
        return (
            <BooleanField
                checked={Boolean(value)}
                onChange={(e: any) => onChange(Boolean(e.target.checked))}
                disabled={disabled}
            />
        );
    }

    if (isArrayOfStringSchema(prop)) {
        return (
            <TagsField
                value={Array.isArray(value) ? value : []}
                onValueChange={(v) => onChange(Array.isArray(v) ? v : [])}
                disabled={disabled}
            />
        );
    }

    // Fallback: raw JSON editor for nested objects / anything we don't model yet
    return <JsonField value={value ?? null} onValueChange={(next: any) => onChange(next)} disabled={disabled} />;
};

export default SchemaForm;
